// Shared Stage Cutout queueing helper (BOU-920).
//
// Used by both:
//   - pages/CharacterCreationPage.jsx — direct /characters editor (post-save)
//   - components/CampaignSetup.jsx     — campaign-setup wizard (post-finalize)
//
// Keeping a single helper here ensures both flows go through the same
// updateCharacterProfile → generateCharacterStageAssets sequence so they
// produce identical Stage Cutout outcomes for newly-created characters.

import apiService from './apiService.js';
import {
  buildCharacterImagePayloadForWrite,
  hasPortraitImageSource,
} from '../utils/characterImageAssets.js';

const buildProfilePayload = (source = {}) => ({
  name: source.name,
  character_class: source.character_class,
  race: source.race,
  level: source.level || 1,
  concept: source.concept,
  description: source.description,
  backstory: source.backstory,
  visual_description: source.visual_description,
  ...buildCharacterImagePayloadForWrite(source),
  gender: source.gender,
  age_category: source.age_category,
  build: source.build,
  height_description: source.height_description,
  facial_expression: source.facial_expression,
  facial_features: source.facial_features,
  attire: source.attire,
  primary_weapon: source.primary_weapon,
  distinguishing_feature: source.distinguishing_feature,
  background_setting: source.background_setting,
  pose: source.pose,
  voice_id: source.voice_id,
  voice_archetype: source.voice_archetype,
  voice_settings: source.voice_settings,
});

const sourceHasPortrait = (character) => hasPortraitImageSource(character);

export const queueStageCutout = async ({
  characterId,
  sourceCharacter,
  options = {},
} = {}) => {
  if (!characterId) return null;
  if (!sourceHasPortrait(sourceCharacter)) return null;

  const {
    regenerate = false,
    persistLatestProfile = true,
    customPromptAdditions = null,
    visualStyle = null,
    imageModel = null,
    sessionId = null,
    onPersisted,
    onError,
  } = options;

  try {
    let persistedCharacter = sourceCharacter;
    if (persistLatestProfile) {
      persistedCharacter = await apiService.updateCharacterProfile(
        characterId,
        buildProfilePayload(sourceCharacter),
      );
      if (typeof onPersisted === 'function') {
        onPersisted(persistedCharacter);
      }
    }
    return await apiService.generateCharacterStageAssets(characterId, {
      regenerate,
      visual_style:
        visualStyle
        || persistedCharacter?.visual_style
        || sourceCharacter?.visual_style
        || null,
      image_model:
        imageModel
        || persistedCharacter?.image_style
        || sourceCharacter?.image_style
        || null,
      custom_prompt_additions: customPromptAdditions,
      session_id: sessionId,
    });
  } catch (err) {
    if (typeof onError === 'function') {
      onError(err);
    } else {
      console.warn('[stageCutoutQueue] Failed to queue Stage Cutout generation:', err);
    }
    return null;
  }
};

/**
 * Queue Stage Cutout generation for every newly-created character produced by
 * /api/campaigns/initialize. Skips reused-existing slots (their cutout pipeline
 * lives on the /characters page) and slots without portrait data.
 *
 * Used by CampaignSetup.finalizeSetup so the wizard mirrors the post-save
 * behavior of the /characters editor.
 *
 * @param {Array<{character_id: string, slot_id: number, reused?: boolean}>} createdCharacters
 * @param {Object<number, object>} slotsBySlotId — slot_id → slot data with portrait/profile fields
 * @returns {Promise<Array>} Resolves once every queue attempt finishes (failures included).
 */
export const queueStageCutoutForCharacters = (createdCharacters = [], slotsBySlotId = {}) => {
  const promises = [];
  for (const created of createdCharacters) {
    if (!created || !created.character_id || created.reused) continue;
    const slotData = slotsBySlotId[created.slot_id];
    if (!slotData) continue;
    // BOU-926: surface missing-portrait skips so callers see them instead of
    // silently dropping the Stage Cutout for a freshly-created character.
    if (!sourceHasPortrait(slotData)) {
      console.warn(
        '[stageCutoutQueue] Skipping Stage Cutout for character',
        created.character_id,
        '— slot data is missing a portrait image ID, URL, or path. Check that the wizard slot population path (e.g. autoFillCharacter, autoFillAllCharacters, selectPregeneratedCharacter) is copying portrait fields from the source character.'
      );
      continue;
    }
    promises.push(
      queueStageCutout({
        characterId: created.character_id,
        sourceCharacter: slotData,
        options: { regenerate: false, persistLatestProfile: false },
      }).catch((err) => {
        console.warn(
          '[stageCutoutQueue] Stage Cutout queue failed for',
          created.character_id,
          err
        );
        return null;
      })
    );
  }
  return Promise.all(promises);
};

export default queueStageCutout;
